import React from 'react';
import { Button, Card } from '../ui';

type BrandIconCardProps = {
  brandIconFileInputRef: React.RefObject<HTMLInputElement>;
  handleBrandIconFileSelect: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  brandIconUrl: string;
  brandIconPrompt: string;
  brandIconCompressNote: string;
  setBrandIconModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  brandIconGenerating: boolean;
  brandIconUploading: boolean;
  brandIconDeleting: boolean;
  handleBrandIconDelete: () => void;
};

export function BrandIconCard({
  brandIconFileInputRef,
  handleBrandIconFileSelect,
  brandIconUrl,
  brandIconPrompt,
  brandIconCompressNote,
  setBrandIconModalOpen,
  brandIconGenerating,
  brandIconUploading,
  brandIconDeleting,
  handleBrandIconDelete,
}: BrandIconCardProps) {
  return (
    <Card elevation="raised">
      <Card.Header>
        <Card.Title subtitle="Your logo or brand mark. The image agent can reference it on demand when the post calls for branded artwork (badges, sign-offs, merch).">
          <span className="settings-section-icon">🏷️</span> Brand Icon
        </Card.Title>
      </Card.Header>
      <Card.Body className="settings-card-body">
        <input
          ref={brandIconFileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handleBrandIconFileSelect}
        />
        <div className="settings-brand-icon-row">
          <div
            className={`settings-brand-icon-preview${brandIconUrl ? '' : ' settings-brand-icon-preview--empty'}`}
            role={brandIconUrl ? undefined : 'button'}
            tabIndex={brandIconUrl ? -1 : 0}
            onClick={() => {
              if (!brandIconUrl) brandIconFileInputRef.current?.click();
            }}
            onKeyDown={(e) => {
              if (!brandIconUrl && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                brandIconFileInputRef.current?.click();
              }
            }}
            aria-label={brandIconUrl ? 'Brand icon preview' : 'Upload brand icon'}
          >
            {brandIconUrl ? (
              <img src={brandIconUrl} alt="Brand icon" />
            ) : (
              <>
                <span style={{ fontSize: 28 }}>🏷️</span>
                <span>Click to upload</span>
              </>
            )}
          </div>
          <div className="settings-brand-icon-info">
            <p className="settings-brand-icon-info__title">
              {brandIconUrl ? 'Your brand icon is set' : 'Add your logo or brand mark'}
            </p>
            <p className="settings-brand-icon-info__desc">
              Generate one with AI or upload your own. The image agent decides per-post whether
              the icon fits the visual. Square PNG with a transparent background works best;
              uploads are shrunk to ~512px to keep things fast.
            </p>
            {brandIconPrompt && (
              <p className="settings-brand-icon-info__compress" title={brandIconPrompt}>
                <strong>Last prompt:</strong> {brandIconPrompt}
              </p>
            )}
            {brandIconCompressNote && (
              <p className="settings-brand-icon-info__compress">{brandIconCompressNote}</p>
            )}
            <div className="settings-brand-icon-actions">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setBrandIconModalOpen(true)}
                loading={brandIconGenerating}
                disabled={brandIconUploading || brandIconDeleting}
              >
                {brandIconUrl ? '✨ Regenerate with AI' : '✨ Generate with AI'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => brandIconFileInputRef.current?.click()}
                loading={brandIconUploading}
                disabled={brandIconGenerating || brandIconDeleting}
              >
                📁 {brandIconUrl ? 'Replace upload' : 'Upload icon'}
              </Button>
              {brandIconUrl && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleBrandIconDelete}
                  loading={brandIconDeleting}
                  disabled={brandIconGenerating || brandIconUploading}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
